import React, { useRef, useState } from "react"

import { FileUp, X } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { useToast } from "@/hooks/use-toast"
import { usePersonalDetails } from "@/hooks/usePersonalDetails"
import { processImageFile } from "@/utils/imageConversion"

interface HeadshotUploadSectionProps {
  headshotFile: File | null
  setHeadshotFile: (file: File | null) => void
  headshotPreview: string | null
  setHeadshotPreview: (preview: string | null) => void
}

const HeadshotUploadSection: React.FC<HeadshotUploadSectionProps> = ({
  headshotFile,
  setHeadshotFile,
  headshotPreview,
  setHeadshotPreview,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isConverting, setIsConverting] = useState(false)
  const { toast } = useToast()

  // Get upload progress from our hook
  const { isHeadshotUploading } = usePersonalDetails()

  const handleFileSelect = async (file: File) => {
    if (file && file.type.startsWith("image/")) {
      try {
        setIsConverting(true)

        // Convert HEIC/HEIF to JPG if needed
        const processedFile = await processImageFile(file, "jpeg")

        setHeadshotFile(processedFile)

        const reader = new FileReader()
        reader.onload = (e) => {
          setHeadshotPreview(e.target?.result as string)
        }
        reader.readAsDataURL(processedFile)
      } catch (error) {
        console.error("Error processing image file:", error)
        toast({
          title: "Conversion Error",
          description:
            error instanceof Error
              ? error.message
              : "Failed to process image file. Please try a different format.",
          variant: "destructive",
        })
      } finally {
        setIsConverting(false)
      }
    }
  }

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const removeFile = (e: React.MouseEvent) => {
    e.stopPropagation()
    setHeadshotFile(null)
    setHeadshotPreview(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-2 text-lg font-semibold text-jungle">Professional Headshot</h3>
        <p className="mb-4 text-gray-600">
          Please upload a professional headshot photo. The photo should have a white backdrop and
          show you clearly from the shoulders up.
        </p>
      </div>

      <div className="space-y-4">
        <Label htmlFor="headshot-upload" className="text-sm font-medium">
          Upload Headshot Photo *
        </Label>

        <div
          className="flex min-h-32 cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed border-gray-300 p-4 transition-colors hover:border-gray-400"
          onClick={triggerFileInput}
        >
          {headshotPreview ? (
            <div className="flex flex-col items-center space-y-2">
              <img
                src={headshotPreview}
                alt="Headshot preview"
                className="max-h-32 object-contain"
              />
              {(isHeadshotUploading || isConverting) && (
                <div className="w-full space-y-2">
                  <p className="text-center text-xs text-gray-500">
                    {isConverting ? "Converting..." : "Uploading..."}
                  </p>
                </div>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={removeFile}
                disabled={isHeadshotUploading || isConverting}
              >
                <X className="mr-2 h-4 w-4" />
                Remove
              </Button>
            </div>
          ) : (
            <>
              <FileUp className="mb-2 h-8 w-8 text-gray-400" />
              <p className="text-sm text-gray-500">Click to upload headshot photo</p>
              <p className="text-xs text-gray-400">PNG, JPG, GIF, HEIC, or HEIF (max. 5MB)</p>
            </>
          )}
        </div>

        <input
          ref={fileInputRef}
          id="headshot-upload"
          type="file"
          className="hidden"
          onChange={handleFileInput}
          accept="image/*"
        />

        {!headshotFile && (
          <p className="text-sm text-red-500">Headshot photo is required to save</p>
        )}
      </div>
    </div>
  )
}

export default HeadshotUploadSection
